/**
 * The strip at the top of a customer's Settings: whose billing account this
 * is, the Sage account it is known by, and where its Xero contact stands
 * (docs/features/credit-control/06_ui_strategy.md §2; the Xero contact is
 * credit control's, spec 11 §6a).
 * - "Billing account CIS:CUS-… · Sage POL:…", or the default's wording while
 *   the customer has no billing account (card only);
 * - the Xero contact, which CIS creates on its first send and never links by
 *   name: "Not in Xero yet", the contact with its ID, or "Name clash in Xero:
 *   needs attention", in amber, linking to the open clash ticket when the view
 *   finds one. Each trading company has its own Xero organisation, so with
 *   more than one company not Stopped there is a contact for each, named for
 *   its company. There is no "Map contact" action: finance resolves a clash in
 *   Xero from that ticket, and the next send retries;
 * - a sub-account names its payer's account and says how the payer pays,
 *   read only (the "Billed to" row below links the payer's Settings);
 * - a payer says how many customers it pays for ("Pays for 3 customers");
 * - Pollards' internal account (own stock, never billed) reads its kind's
 *   wording ("Internal: Pollards' own stock, not billed") and has no Xero
 *   contact line: nothing is ever pushed to Xero for it.
 */
import { Link } from "react-router";
import type {
  BillingAccountXeroContactView,
  CustomerCreditView,
} from "@shared-core/data/credit-control-types";
import { appPathOrNull } from "@shared-core/domain/credit-control-presentation";
import { cn } from "../../primitives/utils";

export function BillingAccountHeader({
  credit,
}: {
  credit: CustomerCreditView;
}) {
  const { payer, billingAccount: account } = credit;
  const isInternal = account?.kind === "internal";
  const contacts = account?.xeroContacts ?? [];
  const clashes = contacts.filter((c) => c.status === "needs_attention");
  const clash = clashes.length > 0;

  return (
    <section
      aria-label="Billing account"
      className={cn(
        "space-y-3 rounded-lg border p-4 text-sm",
        clash
          ? "border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100"
          : "border-border",
      )}
    >
      <p className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        {isInternal && (
          <>
            <span className="font-medium">{account.kindLabel}</span>
            <span aria-hidden="true">·</span>
          </>
        )}
        <span className={isInternal ? undefined : "font-medium"}>
          {payer
            ? account
              ? `${payer.customerName}'s billing account ${account.ref}`
              : `${payer.customerName} has no billing account yet`
            : account
              ? `Billing account ${account.ref}`
              : `No billing account yet · ${credit.methodsLabel}`}
        </span>
        {credit.paidForCount > 0 && (
          <>
            <span aria-hidden="true">·</span>
            <span>
              Pays for {credit.paidForCount}{" "}
              {credit.paidForCount === 1 ? "customer" : "customers"}
            </span>
          </>
        )}
        {account?.sageAccountRef && (
          <>
            <span aria-hidden="true">·</span>
            <span>Sage {account.sageAccountRef}</span>
          </>
        )}
        {!isInternal &&
          (contacts.length === 0 ? (
            <>
              <span aria-hidden="true">·</span>
              <span>Xero contact: Not in Xero yet</span>
            </>
          ) : (
            contacts.map((contact) => (
              <XeroContact
                key={contact.tradingCompanyCode}
                contact={contact}
                account={account}
                named={contacts.length > 1}
              />
            ))
          ))}
      </p>
      {clashes.map((contact) => (
        <p key={contact.tradingCompanyCode}>
          Xero already has a contact with {account?.name}'s name or account
          number
          {contacts.length > 1
            ? ` in ${contact.tradingCompanyName}'s organisation`
            : ""}
          , so One hasn't created a contact. Finance has a ticket to fix the
          clash in Xero. One will try again when the next invoice is sent.
        </p>
      ))}
      {payer && (
        <div className="space-y-3">
          <p className="text-muted-foreground">
            {credit.customerName} is a sub-account: it pays the way{" "}
            {payer.customerName} does, on {payer.customerName}'s billing
            account.
          </p>
          <p>
            {account?.summaryLabel ?? credit.methodsLabel}
            {account?.onStop && (
              <span className="font-medium text-amber-700 dark:text-amber-300">
                {" "}
                · On stop
              </span>
            )}
          </p>
        </div>
      )}
    </section>
  );
}

/** One trading company's contact: named for its company only when there is more than one. */
function XeroContact({
  contact,
  account,
  named,
}: {
  contact: BillingAccountXeroContactView;
  account: NonNullable<CustomerCreditView["billingAccount"]> | null;
  named: boolean;
}) {
  const clash = contact.status === "needs_attention";
  const clashTicket = clash ? appPathOrNull(contact.clashTicketUrl) : null;
  return (
    <>
      <span aria-hidden="true">·</span>
      <span className={clash ? "font-medium" : undefined}>
        Xero contact{named ? ` (${contact.tradingCompanyName})` : ""}:{" "}
        {contact.status === "linked" ? (
          <>
            {contact.xeroContactName ?? account?.name} ({contact.statusLabel}){" "}
            <span className="font-mono text-xs text-muted-foreground">
              {contact.xeroContactId}
            </span>
          </>
        ) : clashTicket ? (
          <Link to={clashTicket} className="underline underline-offset-2">
            {contact.statusLabel}
          </Link>
        ) : (
          contact.statusLabel
        )}
      </span>
    </>
  );
}
